import { useCallback } from 'react';

import { Dialog, type DialogProps } from '@infinex/ui/components';

import { isNativeWebView } from './isNativeWebView';
import { postMessageToNative } from './postMessageToNative';
import { StandaloneDialogProviders } from './StandaloneDialogProviders';
import { useMobileDialogResetContext } from './useMobileDialogReset';

/**
 * Drop-in replacement for `Dialog` that detects if we're inside the Infinex
 * mobile app's WebView. When true, renders `StandaloneDialogProvider` (no
 * modal overlay, close wired to postMessage). When false, renders the standard
 * `Dialog` component with all props forwarded — zero behaviour change for the
 * web-app.
 *
 * This avoids a "dialog-inside-a-sheet" situation: the mobile app already
 * renders the content inside a native action sheet, so we don't want the
 * web-app `Dialog` to also render a modal overlay on top.
 */
export function ConditionalMobileWebViewDialog(props: DialogProps) {
  const scheduleReset = useMobileDialogResetContext();

  const handleClose = useCallback(() => {
    postMessageToNative({ type: 'close' });
    scheduleReset?.();
  }, [scheduleReset]);

  // Dev fallback: `/mobile/*` routes are only loaded inside WebViews, but
  // `__INFINEX_EMBEDDED__` won't be set when testing in a regular browser.
  const isMobileRoute =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/mobile/');

  if (isNativeWebView() || isMobileRoute) {
    const children =
      typeof props.children === 'function'
        ? props.children({ close: handleClose })
        : props.children;

    return (
      <StandaloneDialogProviders onClose={handleClose}>
        {children}
      </StandaloneDialogProviders>
    );
  }

  return <Dialog {...props} />;
}
